import { motion } from 'framer-motion';
import { Briefcase, Plus, Target as TargetIcon, Wallet } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  ProgressBar,
  SwipeRow,
  useToast,
} from '../../components';
import {
  buildCategoryMap,
  selectActiveScheduledItems,
  selectGoalsByDeadline,
  selectSpreadTransactions,
  useActions,
  useAppData,
} from '../../data';
import type { Goal, ScheduledItem, Transaction } from '../../data/types';
import { effectiveSpreadEnd } from '../../lib/budget';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import {
  diffInDays,
  formatPayDate,
  formatPayDateShort,
  fromIsoDate,
  startOfDay,
} from '../../lib/date';
import { resolveIcon } from '../../lib/icons';
import { springGentle, springQuick, tapScale } from '../../tokens';
import { EditTransactionSheet } from '../activity/EditTransactionSheet';
import { BudgetHeatmap } from './BudgetHeatmap';
import { BudgetModelViz } from './BudgetModelViz';
import { CashflowForecast } from './CashflowForecast';
import { CategoryAllocationSheet } from './CategoryAllocationSheet';
import { CollapsibleSection } from './CollapsibleSection';
import { GoalSheet } from './GoalSheet';
import { OneOffIncomeSheet } from './OneOffIncomeSheet';
import { ScheduledItemSheet } from './ScheduledItemSheet';
import { SpreadManageSheet } from './SpreadManageSheet';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const FREQUENCY_LABEL: Record<ScheduledItem['frequency'], string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
  one_time: 'Once',
};

type PlanScreenProps = {
  /** One-shot intent passed by the navigator — e.g. 'addIncome' opens the OneOffIncomeSheet on landing. */
  initialAction?: 'addIncome' | null;
  /** Callback to clear the action so it doesn't replay on subsequent tab visits. */
  onInitialActionConsumed?: () => void;
};

export function PlanScreen({
  initialAction = null,
  onInitialActionConsumed,
}: PlanScreenProps = {}) {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();
  const today = useMemo(() => new Date(), []);

  // Sheet state.
  const [scheduledSheet, setScheduledSheet] = useState<{
    type: 'expense' | 'income';
    mode: 'add' | ScheduledItem;
  } | null>(null);
  const [goalSheet, setGoalSheet] = useState<'add' | Goal | null>(null);
  const [allocCategoryId, setAllocCategoryId] = useState<string | null>(null);
  const [spreadId, setSpreadId] = useState<string | null>(null);
  const [editTxId, setEditTxId] = useState<string | null>(null);
  const [oneOffIncomeOpen, setOneOffIncomeOpen] = useState(false);

  // If we landed here via Today's empty-state CTA, open the income
  // sheet immediately and consume the action so it doesn't fire again.
  useEffect(() => {
    if (initialAction === 'addIncome') {
      setOneOffIncomeOpen(true);
      onInitialActionConsumed?.();
    }
  }, [initialAction, onInitialActionConsumed]);

  const allScheduled = useMemo(
    () => selectActiveScheduledItems(data),
    [data],
  );
  const incomeItems = useMemo(
    () => allScheduled.filter((i) => i.type === 'income'),
    [allScheduled],
  );
  const billItems = useMemo(
    () => allScheduled.filter((i) => i.type === 'expense'),
    [allScheduled],
  );
  const goals = useMemo(() => selectGoalsByDeadline(data), [data]);
  const spreads = useMemo(
    () => selectSpreadTransactions(data, today),
    [data, today],
  );
  const spreadPurchases = useMemo(
    () => spreads.filter((t) => t.type === 'expense'),
    [spreads],
  );
  const spreadPaychecks = useMemo(
    () => spreads.filter((t) => t.type === 'income'),
    [spreads],
  );
  const categoryMap = useMemo(
    () => buildCategoryMap(data.categories),
    [data.categories],
  );

  return (
    <motion.main
      initial="hidden"
      animate="show"
      variants={stagger}
      className="mx-auto max-w-content px-screen-x pt-section-gap"
    >
      <motion.header
        variants={fadeUp}
        transition={springGentle}
        className="mb-section-gap"
      >
        <h1 className="text-section">Plan</h1>
        <p className="mt-1 text-[13px] text-text-secondary">
          Income, bills, goals, and your budget model.
        </p>
      </motion.header>

      <motion.div variants={fadeUp} transition={springGentle}>
        <BudgetModelViz
          data={data}
          today={today}
          onEditAllocation={(id) => setAllocCategoryId(id)}
        />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle}>
        <CashflowForecast data={data} today={today} />
      </motion.div>

      <motion.div variants={fadeUp} transition={springGentle}>
        <BudgetHeatmap data={data} today={today} />
      </motion.div>

      {/* Income */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <CollapsibleSection
          title="Income"
          count={incomeItems.length}
          defaultOpen={incomeItems.length > 0}
        >
          {incomeItems.length === 0 ? (
            <Card flush elevation="sm">
              <div className="px-card-pad py-6 flex flex-col items-center text-center">
                <div className="text-text-tertiary mb-4">
                  <Wallet size={32} strokeWidth={1.5} />
                </div>
                <h3 className="text-section text-text-primary mb-label-gap">
                  No income tracked yet
                </h3>
                <p className="text-body text-text-secondary max-w-[320px]">
                  Recurring paychecks for steady earners, or log
                  one-off paychecks (with optional spread) if your
                  income varies.
                </p>
                <div className="mt-section-gap w-full grid grid-cols-1 gap-2">
                  <Button
                    variant="primary"
                    size="md"
                    fullWidth
                    onClick={() => setOneOffIncomeOpen(true)}
                  >
                    Log a paycheck
                  </Button>
                  <Button
                    variant="ghost"
                    size="md"
                    fullWidth
                    onClick={() =>
                      setScheduledSheet({ type: 'income', mode: 'add' })
                    }
                  >
                    Add recurring income
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card flush elevation="sm" className="py-1">
              {incomeItems.map((item, i) => (
                <div key={item.id}>
                  {i > 0 ? <Divider /> : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteScheduledItem(item.id);
                      toast.show({ message: 'Income removed', kind: 'info' });
                    }}
                    onTap={() =>
                      setScheduledSheet({ type: 'income', mode: item })
                    }
                  >
                    <ScheduledRow
                      item={item}
                      iconColor={
                        categoryMap.get(item.categoryId)?.color ?? '#34C759'
                      }
                      iconName={
                        categoryMap.get(item.categoryId)?.icon ?? 'DollarSign'
                      }
                      currency={data.profile.currency}
                    />
                  </SwipeRow>
                </div>
              ))}
              <div className="px-card-pad pb-2 pt-1 grid grid-cols-2 gap-2">
                <Button
                  variant="ghost"
                  fullWidth
                  size="sm"
                  leadingIcon={<Plus size={16} strokeWidth={1.5} />}
                  onClick={() => setOneOffIncomeOpen(true)}
                >
                  Log a paycheck
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  size="sm"
                  leadingIcon={<Plus size={16} strokeWidth={1.5} />}
                  onClick={() =>
                    setScheduledSheet({ type: 'income', mode: 'add' })
                  }
                >
                  Recurring
                </Button>
              </div>
            </Card>
          )}
        </CollapsibleSection>
      </motion.div>

      {/* Bills */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <CollapsibleSection
          title="Bills & expenses"
          count={billItems.length}
          defaultOpen={billItems.length > 0}
        >
          {billItems.length === 0 ? (
            <Card flush elevation="sm">
              <EmptyState
                icon={<Briefcase size={32} strokeWidth={1.5} />}
                title="No recurring bills yet"
                description="Add rent, subscriptions, and other regular expenses so they're factored into your daily allowance."
                action={{
                  label: 'Add bill',
                  onClick: () =>
                    setScheduledSheet({ type: 'expense', mode: 'add' }),
                }}
              />
            </Card>
          ) : (
            <Card flush elevation="sm" className="py-1">
              {billItems.map((item, i) => (
                <div key={item.id}>
                  {i > 0 ? <Divider /> : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteScheduledItem(item.id);
                      toast.show({ message: 'Bill removed', kind: 'info' });
                    }}
                    onTap={() =>
                      setScheduledSheet({ type: 'expense', mode: item })
                    }
                  >
                    <ScheduledRow
                      item={item}
                      iconColor={
                        categoryMap.get(item.categoryId)?.color ?? '#FF9500'
                      }
                      iconName={
                        categoryMap.get(item.categoryId)?.icon ?? 'CreditCard'
                      }
                      currency={data.profile.currency}
                    />
                  </SwipeRow>
                </div>
              ))}
              <div className="px-card-pad pb-2 pt-1">
                <Button
                  variant="ghost"
                  fullWidth
                  size="sm"
                  leadingIcon={<Plus size={16} strokeWidth={1.5} />}
                  onClick={() =>
                    setScheduledSheet({ type: 'expense', mode: 'add' })
                  }
                >
                  Add bill
                </Button>
              </div>
            </Card>
          )}
        </CollapsibleSection>
      </motion.div>

      {/* Spread purchases — expenses only. Manage sheet exposes
          Mark paid off / Apply payment. */}
      {spreadPurchases.length > 0 ? (
        <motion.div variants={fadeUp} transition={springGentle}>
          <CollapsibleSection
            title="Spread purchases"
            count={spreadPurchases.length}
            defaultOpen
          >
            <Card flush elevation="sm" className="py-1">
              {spreadPurchases.map((tx, i) => (
                <div key={tx.id}>
                  {i > 0 ? (
                    <div className="h-px bg-surface-secondary mx-card-pad" />
                  ) : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteTransaction(tx.id);
                      toast.show({
                        message: 'Spread purchase removed',
                        kind: 'info',
                      });
                    }}
                    onTap={() => setSpreadId(tx.id)}
                  >
                    <SpreadRow
                      tx={tx}
                      category={categoryMap.get(tx.categoryId)}
                      currency={data.profile.currency}
                      today={today}
                    />
                  </SwipeRow>
                </div>
              ))}
            </Card>
          </CollapsibleSection>
        </motion.div>
      ) : null}

      {/* Spread paychecks — income only. Manage sheet exposes
          End spread now / Edit, no payment action. */}
      {spreadPaychecks.length > 0 ? (
        <motion.div variants={fadeUp} transition={springGentle}>
          <CollapsibleSection
            title="Spread paychecks"
            count={spreadPaychecks.length}
            defaultOpen
          >
            <Card flush elevation="sm" className="py-1">
              {spreadPaychecks.map((tx, i) => (
                <div key={tx.id}>
                  {i > 0 ? (
                    <div className="h-px bg-surface-secondary mx-card-pad" />
                  ) : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteTransaction(tx.id);
                      toast.show({
                        message: 'Spread paycheck removed',
                        kind: 'info',
                      });
                    }}
                    onTap={() => setSpreadId(tx.id)}
                  >
                    <SpreadRow
                      tx={tx}
                      category={categoryMap.get(tx.categoryId)}
                      currency={data.profile.currency}
                      today={today}
                    />
                  </SwipeRow>
                </div>
              ))}
            </Card>
          </CollapsibleSection>
        </motion.div>
      ) : null}

      {/* Goals */}
      <motion.div variants={fadeUp} transition={springGentle}>
        <CollapsibleSection
          title="Goals"
          count={goals.length}
          defaultOpen={goals.length > 0}
        >
          {goals.length === 0 ? (
            <Card flush elevation="sm">
              <EmptyState
                icon={<TargetIcon size={32} strokeWidth={1.5} />}
                title="No goals yet"
                description="Set a savings goal: vacation, emergency fund, or a big purchase. Track progress here."
                action={{
                  label: 'Add goal',
                  onClick: () => setGoalSheet('add'),
                }}
              />
            </Card>
          ) : (
            <Card flush elevation="sm" className="py-1">
              {goals.map((goal, i) => (
                <div key={goal.id}>
                  {i > 0 ? <Divider /> : null}
                  <SwipeRow
                    onDelete={() => {
                      actions.deleteGoal(goal.id);
                      toast.show({ message: 'Goal removed', kind: 'info' });
                    }}
                    onTap={() => setGoalSheet(goal)}
                  >
                    <GoalRow
                      goal={goal}
                      currency={data.profile.currency}
                    />
                  </SwipeRow>
                </div>
              ))}
              <div className="px-card-pad pb-2 pt-1">
                <Button
                  variant="ghost"
                  fullWidth
                  size="sm"
                  leadingIcon={<Plus size={16} strokeWidth={1.5} />}
                  onClick={() => setGoalSheet('add')}
                >
                  Add goal
                </Button>
              </div>
            </Card>
          )}
        </CollapsibleSection>
      </motion.div>

      <ScheduledItemSheet
        mode={scheduledSheet?.mode ?? null}
        type={scheduledSheet?.type ?? 'expense'}
        onClose={() => setScheduledSheet(null)}
        onSave={(input) => {
          if (input.mode === 'add') {
            actions.addScheduledItem(input.item);
            toast.show({
              message: input.item.type === 'expense' ? 'Bill saved' : 'Income saved',
              kind: 'success',
            });
          } else {
            actions.updateScheduledItem(input.id, input.patch);
            toast.show({ message: 'Updated', kind: 'success' });
          }
        }}
        onDelete={(id) => {
          actions.deleteScheduledItem(id);
          toast.show({ message: 'Deleted', kind: 'info' });
        }}
      />

      <GoalSheet
        mode={goalSheet}
        onClose={() => setGoalSheet(null)}
        onSave={(input) => {
          if (input.mode === 'add') {
            actions.addGoal(input.goal);
            toast.show({ message: 'Goal saved', kind: 'success' });
          } else {
            actions.updateGoal(input.id, input.patch);
            toast.show({ message: 'Goal updated', kind: 'success' });
          }
        }}
        onDelete={(id) => {
          actions.deleteGoal(id);
          toast.show({ message: 'Goal deleted', kind: 'info' });
        }}
      />

      <CategoryAllocationSheet
        category={
          allocCategoryId
            ? data.categories.find((c) => c.id === allocCategoryId) ?? null
            : null
        }
        currency={data.profile.currency}
        onClose={() => setAllocCategoryId(null)}
        onSave={(id, monthly) => {
          actions.updateCategory(id, {
            zeroBasedBudget: monthly > 0 ? monthly : undefined,
          });
          toast.show({ message: 'Allocation saved', kind: 'success' });
        }}
      />

      <SpreadManageSheet
        transaction={
          spreadId
            ? data.transactions.find((t) => t.id === spreadId) ?? null
            : null
        }
        category={
          spreadId
            ? categoryMap.get(
                data.transactions.find((t) => t.id === spreadId)?.categoryId ??
                  '',
              )
            : undefined
        }
        currency={data.profile.currency}
        today={today}
        onClose={() => setSpreadId(null)}
        onPatch={(id, patch) => {
          actions.updateTransaction(id, patch);
          if (patch.spreadEndedOn) {
            toast.show({
              message: 'Future budget updated',
              kind: 'success',
            });
          }
        }}
        onEdit={(tx) => {
          setSpreadId(null);
          setEditTxId(tx.id);
        }}
      />

      <EditTransactionSheet
        transaction={
          editTxId
            ? data.transactions.find((t) => t.id === editTxId) ?? null
            : null
        }
        onClose={() => setEditTxId(null)}
        onUpdate={(id, draft) => {
          actions.updateTransaction(id, {
            type: draft.type,
            description: draft.description,
            categoryId: draft.categoryId,
            date: draft.date,
            amount: draft.amount,
            spreadDays: draft.spreadDays,
          });
          toast.show({ message: 'Transaction updated', kind: 'success' });
        }}
      />

      <OneOffIncomeSheet
        open={oneOffIncomeOpen}
        onClose={() => setOneOffIncomeOpen(false)}
        onSave={(draft) => {
          if (draft.amount <= 0) {
            toast.show({
              message: 'Enter an amount above $0',
              kind: 'warning',
            });
            return;
          }
          actions.addTransaction({
            amount: draft.amount,
            type: 'income',
            categoryId: draft.categoryId,
            description: draft.description || 'Paycheck',
            date: draft.date,
            source: 'manual',
            spreadDays: draft.spreadDays,
          });
          toast.show({
            message: draft.spreadDays && draft.spreadDays > 1
              ? `Spread $${draft.amount.toFixed(2)} over ${draft.spreadDays} days`
              : `Logged $${draft.amount.toFixed(2)} income`,
            kind: 'success',
          });
        }}
      />
    </motion.main>
  );
}

function SpreadRow({
  tx,
  category,
  currency,
  today,
  onClick,
}: {
  tx: Transaction;
  category:
    | ReturnType<typeof useAppData>['categories'][number]
    | undefined;
  currency: string;
  today: Date;
  /** Optional — when wrapped in SwipeRow, taps are handled by the parent. */
  onClick?: () => void;
}) {
  const Icon = resolveIcon(category?.icon ?? 'CalendarRange');
  const days = tx.spreadDays && tx.spreadDays > 1 ? tx.spreadDays : 1;
  const dailyRate = days > 0 ? tx.amount / days : 0;
  const start = startOfDay(fromIsoDate(tx.date));
  const effEnd = effectiveSpreadEnd(tx);
  const t0 = startOfDay(today);
  // totalDays = the user's intended window. Don't derive it from
  // effEnd - start, since spreadEndedOn (paid-off shifts) can land
  // effEnd before start and produce a nonsensical "0 of 0".
  // NOTE: diffInDays(a, b) returns b - a — earlier date first when you
  // want a positive count. Reversed args produce negatives that clamp
  // to 0 via Math.max(0, …) and produced the "0 days left of 14" bug.
  const totalDays = days;
  const remainingDays = Math.max(0, diffInDays(t0, effEnd));
  const elapsed = Math.min(
    totalDays,
    Math.max(0, diffInDays(start, t0) + 1),
  );
  const progress = totalDays > 0 ? Math.min(1, elapsed / totalDays) : 1;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      className="w-full flex flex-col gap-2 py-3 px-card-pad text-left min-h-tap"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center"
          style={{
            backgroundColor: `${category?.color ?? '#8E8E93'}26`,
            color: category?.color ?? '#8E8E93',
          }}
        >
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-text-primary truncate">
            {tx.description?.trim() || category?.name || 'Spread purchase'}
          </div>
          <div className="text-[12px] text-text-secondary mt-0.5 truncate">
            {formatCurrency(tx.amount, { currency })} ·{' '}
            {formatCurrency(dailyRate, { currency })}/day
          </div>
        </div>
        <div className="text-[12px] font-mono tabular-nums text-text-secondary shrink-0 text-right">
          <div>
            {remainingDays} day{remainingDays === 1 ? '' : 's'} left
          </div>
          <div className="text-text-tertiary">
            of {totalDays}
          </div>
        </div>
      </div>
      <div className="ml-[52px] h-[3px] rounded-pill bg-surface-secondary overflow-hidden">
        <div
          className="block h-full rounded-pill bg-accent transition-all"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </motion.button>
  );
}

// ---------- Rows ----------

function Divider() {
  return <div className="h-px bg-surface-secondary mx-card-pad" />;
}

function ScheduledRow({
  item,
  iconColor,
  iconName,
  currency,
  onClick,
}: {
  item: ScheduledItem;
  iconColor: string;
  iconName: string;
  currency: string;
  /** Optional — when wrapped in SwipeRow, taps are handled by the parent. */
  onClick?: () => void;
}) {
  const Icon = resolveIcon(iconName);
  const isExpense = item.type === 'expense';
  const nextDue = formatPayDateShort(fromIsoDate(item.nextDate));

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      className="w-full flex items-center gap-3 py-3 px-card-pad text-left min-h-tap"
    >
      <div
        className="w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center"
        style={{ backgroundColor: `${iconColor}26`, color: iconColor }}
      >
        <Icon size={20} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-medium text-text-primary truncate">
            {item.name}
          </span>
          <FrequencyBadge frequency={item.frequency} />
        </div>
        <div className="text-[12px] text-text-secondary mt-0.5 truncate">
          {isExpense ? 'Next due ' : 'Next on '}
          {nextDue}
        </div>
      </div>
      <div
        className={cn(
          'text-[15px] font-mono tabular-nums shrink-0',
          isExpense ? 'text-negative' : 'text-positive',
        )}
      >
        {isExpense ? '-' : '+'}
        {formatCurrency(item.amount, { currency })}
      </div>
    </motion.button>
  );
}

function FrequencyBadge({
  frequency,
}: {
  frequency: ScheduledItem['frequency'];
}) {
  return (
    <span className="inline-flex items-center justify-center rounded-pill px-1.5 py-[1px] bg-surface-secondary text-text-tertiary text-[9px] font-medium uppercase tracking-[0.04em] leading-none">
      {FREQUENCY_LABEL[frequency]}
    </span>
  );
}

function GoalRow({
  goal,
  currency,
  onClick,
}: {
  goal: Goal;
  currency: string;
  /** Optional — when wrapped in SwipeRow, taps are handled by the parent. */
  onClick?: () => void;
}) {
  const Icon = resolveIcon(goal.icon);
  const progress =
    goal.targetAmount > 0
      ? Math.min(1, goal.currentAmount / goal.targetAmount)
      : 0;
  const pct = Math.round(progress * 100);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      className="w-full flex flex-col gap-2 py-3 px-card-pad text-left min-h-tap"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-button shrink-0 inline-flex items-center justify-center bg-accent/10 text-accent">
          <Icon size={20} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-text-primary truncate">
            {goal.name}
          </div>
          <div className="text-[12px] text-text-secondary mt-0.5">
            {goal.deadline ? (
              <>by {formatPayDate(fromIsoDate(goal.deadline))} · </>
            ) : null}
            {pct}% complete
          </div>
        </div>
        <div className="text-[14px] font-mono tabular-nums text-text-secondary shrink-0">
          {formatCurrency(goal.currentAmount, { currency })}{' '}
          <span className="text-text-tertiary">
            / {formatCurrency(goal.targetAmount, { currency })}
          </span>
        </div>
      </div>
      <ProgressBar value={progress} tone="accent" className="ml-[52px]" />
    </motion.button>
  );
}
